/**
 * App-wide sync: when the ongoing job API returns an ingest job_id, rehydrate
 * the ingest job store so progress is restored after page refresh.
 * Renders nothing; mount once in MainLayout.
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/auth/useAuth';
import { useOrg } from '@/hooks/org/useOrg';
import { useIngestOngoingJob } from '@/hooks/security-scanner';
import { useIngestJobStore } from '@/stores/ingestJobStore';

export function IngestOngoingJobSync() {
  const { user } = useAuth();
  const { selectedOrg } = useOrg();
  const orgId = selectedOrg?.id ?? '';
  const userId = user?.id ?? '';
  const { data } = useIngestOngoingJob({
    org_id: orgId || undefined,
    user_id: userId || undefined,
  });
  const rehydratedJobIdRef = useRef<string | null>(null);
  const rehydrateFromOngoingJob = useIngestJobStore((s) => s.rehydrateFromOngoingJob);

  useEffect(() => {
    const jobId = data?.job_id;
    if (!jobId || !orgId || !userId) return;
    const { isIngesting, jobId: currentJobId } = useIngestJobStore.getState().state;
    if (isIngesting && currentJobId === jobId) return;
    if (rehydratedJobIdRef.current === jobId) return;

    rehydratedJobIdRef.current = jobId;
    rehydrateFromOngoingJob(orgId, String(userId), jobId);
  }, [data?.job_id, orgId, userId, rehydrateFromOngoingJob]);

  return null;
}
